<template>
  <div class="checkout-container">
    <div class="checkout-content">
      <div v-if="!hasError">
        <!-- 初始載入階段 -->
        <div v-if="!isPaymentInProgress && !paymentWindowOpened">
          <h1 v-if="mode === 'subscription'">正在導向至藍新金流訂閱授權...</h1>
          <h1 v-else>正在導向至藍新金流...</h1>
          <div class="loading">
            <div class="spinner"></div>
            <p v-if="mode === 'subscription'">請稍候，正在準備訂閱授權頁面</p>
            <p v-else>請稍候，正在準備付款頁面</p>
          </div>
        </div>

        <!-- 新視窗已開啟，等待用戶操作 -->
        <div v-else-if="paymentWindowOpened && !paymentCompleted" class="payment-window-guidance">
          <h1 v-if="isInAppBrowser && popupBlocked">請使用外部瀏覽器開啟</h1>
          <h1 v-else-if="popupBlocked">前往付款</h1>
          <h1 v-else>請在付款視窗完成付款</h1>
          <p v-if="agreementAttempt" class="agreement-consent">{{ agreementConsentText }}</p>
          <p v-if="agreementAttempt && agreementIsTrial" class="agreement-consent">將驗證 NT$1 並立即取消，不會扣款；試用期結束後才開始收費。</p>

          <!-- 如果偵測到 in-app browser，優先顯示使用外部瀏覽器的引導 -->
          <div v-if="isInAppBrowser && popupBlocked">
            <div class="guidance-content">
              <div class="warning-icon">🌐</div>
              <h4>偵測到您正在使用 {{ detectedApp || 'App 內建' }} 瀏覽器</h4>
              <p class="notice-description">
                為確保付款安全，請在外部瀏覽器中開啟此頁面完成付款
              </p>
              
              <!-- Lark 專用引導 -->
              <div v-if="detectedApp === 'Lark'" class="app-specific-guide">
                <div class="guide-step">
                  <span class="step-icon">👆</span>
                  <div class="step-content">
                    <strong>步驟 1：</strong>點擊右上角「<span class="menu-icon">⋯</span>」
                  </div>
                </div>
                <div class="guide-step">
                  <span class="step-icon">🌐</span>
                  <div class="step-content">
                    <strong>步驟 2：</strong>選擇「使用瀏覽器開啟」
                  </div>
                </div>
                <div class="guide-step">
                  <span class="step-icon">✅</span>
                  <div class="step-content">
                    <strong>步驟 3：</strong>在外部瀏覽器中完成付款流程
                  </div>
                </div>
              </div>
              
              <!-- LINE 專用引導 -->
              <div v-else-if="detectedApp === 'LINE'" class="app-specific-guide">
                <div class="guide-step">
                  <span class="step-icon">👆</span>
                  <div class="step-content">
                    <strong>步驟 1：</strong>點擊右下角「<span class="menu-icon vertical-dots">⋮</span>」
                  </div>
                </div>
                <div class="guide-step">
                  <span class="step-icon">🌐</span>
                  <div class="step-content">
                    <strong>步驟 2：</strong>選擇「使用預設瀏覽器開啟」
                  </div>
                </div>
                <div class="guide-step">
                  <span class="step-icon">✅</span>
                  <div class="step-content">
                    <strong>步驟 3：</strong>在外部瀏覽器中完成付款流程
                  </div>
                </div>
              </div>
              
              <!-- 其他 App 通用引導 -->
              <div v-else class="app-specific-guide">
                <div class="guide-step">
                  <span class="step-icon">👆</span>
                  <div class="step-content">
                    <strong>步驟 1：</strong>點擊「<span class="menu-icon">⋯</span>」或「更多」選項
                  </div>
                </div>
                <div class="guide-step">
                  <span class="step-icon">🌐</span>
                  <div class="step-content">
                    <strong>步驟 2：</strong>選擇「在瀏覽器中開啟」或「在 Safari/Chrome 中開啟」
                  </div>
                </div>
                <div class="guide-step">
                  <span class="step-icon">✅</span>
                  <div class="step-content">
                    <strong>步驟 3：</strong>在外部瀏覽器中完成付款流程
                  </div>
                </div>
              </div>
              
              <div class="browser-suggestions">
                <p>建議使用：Safari、Chrome、Edge 或 Firefox</p>
              </div>
            
              <div class="backup-actions">
                <button @click="goBack" class="cancel-btn">取消付款</button>
              </div>
            </div>
          </div>
          
          <!-- 一般瀏覽器：顯示付款視窗引導 -->
          <div v-else>
            <div class="guidance-content">
              <!-- GHL 的 Pay 點擊不會傳進跨網域 iframe，自動開窗多半被擋，故以一般步驟呈現而非錯誤 -->
              <div v-if="popupBlocked" class="open-payment-step">
                <p>付款將在藍新金流的安全頁面進行，點擊後會開啟新視窗</p>
                <button @click="openPaymentWindowManually" class="primary-open-btn">
                  前往藍新付款頁面
                </button>
              </div>
              
              <!-- 備用按鈕 -->
              <div class="backup-actions">
                <button v-if="!popupBlocked" @click="openPaymentWindowManually" class="backup-btn">
                  付款視窗沒有開啟？點此重新開啟
                </button>
                <button @click="goBack" class="cancel-btn">取消付款</button>
              </div>
            </div>
            
            <!-- 狀態指示器 -->
            <div v-if="!popupBlocked" class="status-indicator">
              <div class="pulse-dot"></div>
              <span>等待付款完成...</span>
            </div>
          </div>
        </div>

        <!-- 付款完成 -->
        <div v-else-if="paymentCompleted" class="payment-success">
          <div class="success-icon">✅</div>
          <h1>付款處理中</h1>
          <p>正在確認付款結果，請稍候...</p>
        </div>
      </div>
      
      <!-- 錯誤顯示區域 -->
      <div v-else class="error-container">
        <div class="error-icon">⚠️</div>
        <h1>付款處理失敗</h1>
        <div class="error-message">
          <p>{{ errorMessage }}</p>
        </div>
        <div class="error-actions">
          <button @click="retryPayment" class="retry-btn" :disabled="isRetrying">
            {{ isRetrying ? '重試中...' : '重新嘗試' }}
          </button>
          <button @click="goBack" class="back-btn">返回</button>
        </div>
        
        <!-- 詳細錯誤信息（開發用） -->
        <details v-if="debugInfo" class="debug-info">
          <summary>技術詳情</summary>
          <pre>{{ debugInfo }}</pre>
        </details>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * NewebPay 結帳頁面元件
 * 負責處理付款流程的導向與顯示
 */
import { clientLogger } from '@/utils/clientLogger';
import axios from 'axios';
import { isTrustedGhlMessage, postToGhl, startReadyResendLoop } from '../utils/ghlMessaging';
import {
  buildProviderReadyPayload,
  buildSuccessResponsePayload,
  buildErrorResponsePayload,
  buildCloseResponsePayload,
  parsePaymentInitiateProps,
  parseSetupInitiateProps,
} from '../utils/ghlEventPayloads';
import { pollAgreementStatus } from '../utils/checkoutAgreementPoll';

let activePaymentWindow = null;

function paymentMessageSummary(data = {}) {
  return {
    type: data.type,
    hasAmount: data.amount !== undefined && data.amount !== null,
    hasOrderId: Boolean(data.orderId || data.order_id),
    hasTransactionId: Boolean(data.transactionId || data.transaction_id),
    hasLocationId: Boolean(data.locationId || data.location_id),
    hasCustomerInfo: Boolean(data.customerInfo || data.contact),
  };
}

export default {
  name: 'NewebPayCheckout',
  data() {
    /**
     * 元件的資料模型
     * @returns {object} - 包含結帳相關資料的物件
     */
    return {
      // GHL 付款資料
      paymentData: null,
      amount: null,
      currency: null,
      orderId: null,
      transactionId: null,
      customerInfo: null,
      // 訂閱支付相關資料
      mode: 'payment', // 'payment' 或 'subscription'
      subscriptionId: null,
      productDetails: null,
      // 其他付款相關資料
      isReady: false,
      isPaymentInProgress: false, // 新增：追蹤付款流程狀態
      // 錯誤處理相關
      hasError: false,
      errorMessage: '',
      debugInfo: null,
      isRetrying: false
      ,
      // 與父視窗溝通的握手狀態
      readyAckReceived: false,
      readySendAttempts: 0,
      readyResendTimer: null,
      // 付款視窗參考（若以新視窗開啟）
      paymentWindowName: null,
      // 新增：付款流程狀態追蹤
      paymentWindowOpened: false,
      paymentCompleted: false,
      popupBlocked: false,
      windowCheckInterval: null,
      paymentStatusPollTimer: null,
      merchantOrderNo: null,
      paymentFormHTML: null, // 儲存付款表單HTML以供重新開啟使用
      isInAppBrowser: false, // 偵測是否在 in-app browser 中
      detectedApp: '', // 偵測到的 app 名稱
      // 結帳綁卡（訂閱）：attempt 由後端建立，success response 只在 attempt COMPLETED 後送出
      agreementAttempt: null,
      agreementPoller: null,
      agreementIsTrial: false,
      agreementConsentText: '本人同意以本次授權建立約定信用卡付款，後續各期由商店依訂閱內容自動扣款。',
    };
  },
  mounted() {
    /**
     * 元件掛載後的初始化邏輯
     * 向父視窗發送準備就緒訊息並設定訊息監聽器
     * 注意：付款頁面不需要 getUserData，所有資訊通過 postMessage 傳遞
     */
    console.log('NewebPayCheckout component mounted');
    console.log('Checkout context:', {
      origin: window.location.origin,
      pathname: window.location.pathname,
      isInIframe: window.parent !== window,
    });
    
    // 偵測 in-app browser
    const browserInfo = clientLogger.detectInAppBrowser();
    this.isInAppBrowser = browserInfo.isInAppBrowser;
    this.detectedApp = browserInfo.detectedApp || '';
    
    // 記錄頁面載入
    clientLogger.info('payment_page_loaded', {
      mode: this.mode,
      hasPaymentData: !!this.paymentData,
      referrer: document.referrer,
      isInIframe: window.parent !== window,
    });
    
    // 檢查 URL 參數是否包含付款資訊
    this.checkUrlParameters();

    // 若非 iframe 環境且網址沒有足夠的付款參數，立即提示而非等待逾時
    const isInIframe = window.parent !== window;
    if (!isInIframe) {
      const urlParams = new URLSearchParams(window.location.search);
      const amount = urlParams.get('amount');
      const orderId = urlParams.get('orderId') || urlParams.get('order_id');
      const transactionId = urlParams.get('transactionId') || urlParams.get('transaction_id');
      const locationId = urlParams.get('locationId') || urlParams.get('location_id');
      const hasSufficientParams = !!(amount && (orderId || transactionId) && locationId);
      if (!hasSufficientParams) {
        this.showError('此頁面需要由 GHL 付款連結開啟或在網址提供必要參數（amount、orderId/transactionId、locationId）。', {
          parentExists: false,
          currentUrl: window.location.href
        });
        return; // 不再嘗試與父視窗互動
      }
    }
    
    // 監聽來自 GHL 的訊息（先掛監聽器再送 ready）
    window.addEventListener('message', this.handleGHLMessage);

    // 更穩定的握手：立即送出、並在取得回應前重試
    const sendReady = () => {
      try {
        const payload = buildProviderReadyPayload({ addCardOnFileSupported: true });
        console.log('Sending custom_provider_ready message to parent (attempt:', this.readySendAttempts + 1, ')');
        postToGhl(window.parent, payload);
        this.readySendAttempts += 1;
      } catch (error) {
        console.error('Failed to send message to parent:', error);
      }
    };

    // 立即送一次，並啟動重試：每 500ms 發送一次，直到收到回應或達到上限（最長約 20 秒）
    // readySendAttempts 是唯一的次數來源（含下方 focus 監聽器觸發的額外發送），
    // 由 startReadyResendLoop 以 hasReachedMaxAttempts 讀取，而非另外自行計數。
    this.readyResendTimer = startReadyResendLoop({
      send: sendReady,
      isAcknowledged: () => this.readyAckReceived || Boolean(this.paymentData),
      hasReachedMaxAttempts: () => this.readySendAttempts >= 40,
      onAcknowledgedStop: () => {
        console.log('Handshake acknowledged; stop resending ready message');
        this.readyResendTimer = null;
      },
      onGiveUp: () => {
        console.warn('No response to custom_provider_ready after multiple attempts');
        this.readyResendTimer = null;
      },
    });

    // 當視窗重新取得焦點也嘗試再送一次（處理某些瀏覽器/IFrame 延遲初始化）
    window.addEventListener('focus', sendReady, { once: true });
    
    this.isReady = true;
    
    // 設定一個超時檢查，如果在 10 秒內沒收到付款資料就顯示錯誤
    setTimeout(() => {
      if (!this.paymentData && !this.hasError) {
        console.warn('No payment data received within 30 seconds');
        this.showError('等待付款資料超時，可能是 GHL 通信問題', {
          timeout: true,
          waitTime: 30000,
          currentUrl: window.location.href,
          parentExists: window.parent !== window,
          urlParams: Object.fromEntries(new URLSearchParams(window.location.search))
        });
      }
    }, 30000);
  },
  beforeUnmount() {
    /**
     * 元件銷毀前的清理工作
     */
    console.log('NewebPayCheckout component being destroyed');
    
    // 記錄頁面卸載
    clientLogger.info('payment_page_unloaded', {
      paymentCompleted: this.paymentCompleted,
      paymentWindowOpened: this.paymentWindowOpened,
      hadError: this.hasError,
    });
    
    // 清理訊息監聽器
    window.removeEventListener('message', this.handleGHLMessage);
    
    // 清理計時器
    if (this.readyResendTimer) {
      this.readyResendTimer.stop();
      this.readyResendTimer = null;
    }

    if (this.windowCheckInterval) {
      clearInterval(this.windowCheckInterval);
      this.windowCheckInterval = null;
    }

    if (this.paymentStatusPollTimer) {
      clearTimeout(this.paymentStatusPollTimer);
      this.paymentStatusPollTimer = null;
    }

    if (this.agreementPoller) {
      this.agreementPoller.stop();
      this.agreementPoller = null;
    }

    // 關閉付款視窗（如果存在且仍開啟）
    const paymentWindow = this.getPaymentWindowRef();
    if (paymentWindow && !paymentWindow.closed) {
      try {
        paymentWindow.close();
      } catch (e) {
        console.warn('無法關閉付款視窗:', e);
      }
    }
    this.clearPaymentWindowRef();
  },
  methods: {
    /**
     * 取得目前的付款視窗參考（非響應式儲存，避免跨網域安全錯誤）
     */
    getPaymentWindowRef() {
      return activePaymentWindow;
    },

    /**
     * 設定目前的付款視窗參考
     * @param {Window|null} win
     */
    setPaymentWindowRef(win) {
      activePaymentWindow = win;
    },

    /**
     * 清除付款視窗參考
     */
    clearPaymentWindowRef() {
      activePaymentWindow = null;
    },

    /**
     * 檢查 URL 參數是否包含付款資訊
     */
    checkUrlParameters() {
      const urlParams = new URLSearchParams(window.location.search);
      const urlHash = window.location.hash;
      
      console.log('URL payment parameter check:', {
        keys: [...urlParams.keys()],
        hasHash: Boolean(urlHash),
      });
      
      // 檢查常見的付款參數
      const amount = urlParams.get('amount');
      const orderId = urlParams.get('orderId') || urlParams.get('order_id');
      const transactionId = urlParams.get('transactionId') || urlParams.get('transaction_id');
      const locationId = urlParams.get('locationId') || urlParams.get('location_id');
      
      if (amount || orderId || transactionId || locationId) {
        console.log('URL payment parameters found:', {
          hasAmount: Boolean(amount),
          hasOrderId: Boolean(orderId),
          hasTransactionId: Boolean(transactionId),
          hasLocationId: Boolean(locationId),
        });
        
        // 如果在 URL 中找到付款資訊，建立付款資料物件
        const paymentDataFromUrl = {
          type: 'payment_initiate_props',
          amount: amount ? parseFloat(amount) : null,
          orderId,
          transactionId,
          locationId,
          currency: urlParams.get('currency') || 'TWD',
          customerInfo: {
            email: urlParams.get('email')
          }
        };
        
        console.log('URL payment data prepared:', paymentMessageSummary(paymentDataFromUrl));
        
        // 如果有足夠的資訊，直接處理付款
        if (amount && (orderId || transactionId)) {
          this.handlePaymentInitiateProps(paymentDataFromUrl);
        }
      }
    },

    /**
     * 處理來自 GHL 的訊息
     * @param {MessageEvent} event - 訊息事件物件
     */
    handleGHLMessage(event) {
      console.log('Received GHL postMessage:', {
        origin: event.origin,
        data: paymentMessageSummary(typeof event.data === 'object' ? event.data : {}),
        source: event.source === window.parent ? 'parent' : 'other',
      });
      if (!isTrustedGhlMessage(event)) {
        console.log(`❌ 忽略來自非預期來源 (${event.origin}) 的訊息`);
        return;
      }

      console.log('=== GHL 訊息處理器 ===');
      console.log('已通過來源驗證，正在處理訊息...');
      
      let messageData = event.data;
      
      // 如果是字串，嘗試解析為 JSON
      if (typeof messageData === 'string') {
        try {
          messageData = JSON.parse(messageData);
          console.log('Parsed string GHL message:', paymentMessageSummary(messageData));
        } catch (parseError) {
          console.log('Failed to parse string as JSON, treating as raw string');
          // 如果不是 JSON，可能是其他格式的訊息，直接返回
          return;
        }
      }
      
      // Keep the last known working handshake behavior: any trusted message
      // from the parent establishes the channel before payment props arrive.
      this.readyAckReceived = true;
      if (this.readyResendTimer) {
        this.readyResendTimer.stop();
        this.readyResendTimer = null;
        console.log('停止重送 custom_provider_ready：已收到父視窗訊息');
      }

      if (messageData && messageData.type === 'setup_initiate_props') {
          console.log('Received setup initialization:', paymentMessageSummary(messageData));
        this.handleSetupInitiateProps(messageData);
      } else if (messageData && messageData.type === 'payment_initiate_props') {
          console.log('Received payment initialization:', paymentMessageSummary(messageData));
        this.handlePaymentInitiateProps(messageData);
      } else if (messageData && messageData.type === 'payment_initiate') {
          console.log('Received legacy payment initialization:', paymentMessageSummary(messageData));
        this.handlePaymentInitiateProps(messageData);
      } else if (messageData && (messageData.type === 'payment_data' || messageData.type === 'payment_info')) {
          console.log('Received payment data:', paymentMessageSummary(messageData));
        this.handlePaymentInitiateProps(messageData);
      } else if (messageData && (
        messageData.type === 'custom_element_success_response' ||
        messageData.type === 'custom_element_error_response' ||
        messageData.type === 'custom_element_close_response'
      )) {
        // 這些通常來自我們的 ReturnURL 頁面（在彈出視窗），需要轉發給 GHL 父視窗
        console.log('↪️ 收到 ReturnURL 結果，轉發給父視窗:', messageData.type);
        
        // 更新付款完成狀態
        if (messageData.type === 'custom_element_success_response') {
          clientLogger.info('payment_success_received', {
            transactionId: messageData.transactionId,
            amount: messageData.amount,
            messageType: messageData.type,
          });
          
          this.paymentCompleted = true;
          this.isPaymentInProgress = false;
          console.log('✅ 付款成功，更新界面狀態');
        } else if (messageData.type === 'custom_element_error_response') {
          clientLogger.error('payment_error_received', {
            message: messageData.message,
            messageType: messageData.type,
          });
          
          this.showError(messageData.message || '付款處理失敗', {
            messageType: messageData.type,
          });
          return; // 不需要轉發錯誤，直接顯示
        }
        
        try {
          postToGhl(window.parent, messageData);
        } catch (e) {
          console.warn('轉發給父視窗失敗');
        }
        // 強制關閉已開啟的付款視窗（多次嘗試）
        this.forceClosePaymentWindow();
        
        // 清理狀態
        this.isPaymentInProgress = false;
        if (this.windowCheckInterval) {
          clearInterval(this.windowCheckInterval);
          this.windowCheckInterval = null;
        }
      } else if (messageData && messageData.amount) {
        // 如果訊息包含 amount 但沒有 type，可能是直接的付款資料
        console.log('Received payment data without type:', paymentMessageSummary(messageData));
        this.handlePaymentInitiateProps({ type: 'payment_initiate_props', ...messageData });
      } else if (messageData && messageData.type) {
        console.log('Received other GHL message type:', messageData.type);
      } else {
        console.log('Received unrecognized GHL message format');
      }
    },

    /**
     * 處理新增付款方式初始化資料
     * @param {object} messageData - setup 資料
     */
    async handleSetupInitiateProps(messageData) {
      try {
        if (!messageData || messageData.mode !== 'setup') {
          return;
        }

        const parsedSetup = parseSetupInitiateProps(messageData);
        const contactId = parsedSetup.contactId;
        const locationId = parsedSetup.locationId;
        const currency = parsedSetup.currency;

        if (!contactId || !locationId || !currency) {
          throw new Error('缺少必要參數（contactId、locationId、currency）');
        }

        this.mode = 'setup';
        this.isPaymentInProgress = true;

        const contextResponse = await axios.post('/api/payment-method/context', {
          locationId,
          contactId,
          publishableKey: parsedSetup.publishableKey,
          contactName: parsedSetup.contactName,
        });
        const contextToken = contextResponse.data?.contextToken;
        if (!contextToken) throw new Error('無法建立付款上下文');
        const response = await axios.post('/api/payment-method/authorize', { contextToken });

        if (!response.data?.success || !response.data?.formHtml) {
          throw new Error(response.data?.message || '產生授權表單失敗');
        }

        const container = document.createElement('div');
        container.style.display = 'none';
        container.innerHTML = response.data.formHtml;
        document.body.appendChild(container);

        const form = container.querySelector('form');
        if (!form) {
          throw new Error('授權表單缺少 form 標籤');
        }

        setTimeout(() => {
          form.submit();
        }, 50);
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || '新增付款方式初始化失敗';
        this.showError(message, error);
        this.sendSetupError(message);
      }
    },

    /**
     * 發送新增付款方式錯誤事件給 GHL
     * @param {string} message - 錯誤訊息
     */
    sendSetupError(message) {
      const payload = buildErrorResponsePayload({ description: message });

      postToGhl(window.parent, payload);
    },

    /**
     * 處理付款初始化資料
     * @param {object} messageData - 付款資料
     */
    handlePaymentInitiateProps(messageData) {
      // 檢查是否已經有付款資料且內容相同，避免重複處理
      if (this.paymentData && 
          this.paymentData.amount === messageData.amount &&
          this.paymentData.orderId === (messageData.orderId || messageData.order_id) &&
          this.paymentData.transactionId === (messageData.transactionId || messageData.transaction_id)) {
        console.log('收到重複的付款資料，跳過處理');
        return;
      }
      
      // 如果已經在處理付款流程，也跳過
      if (this.isPaymentInProgress || this.paymentWindowOpened || this.paymentCompleted) {
        console.log('付款流程已在進行中，跳過新的付款資料');
        return;
      }
      
      console.log('處理新的付款初始化資料...');
      
      // 儲存完整的付款資料
      this.paymentData = messageData;
      this.customerInfo = messageData.customerInfo || messageData.customer;

      // 提取主要付款資訊（含試用期訂閱的 productDetails 金額 fallback）
      const parsedPayment = parsePaymentInitiateProps(messageData);
      this.amount = parsedPayment.amount;
      this.currency = parsedPayment.currency;
      this.orderId = parsedPayment.orderId;
      this.transactionId = parsedPayment.transactionId;
      this.mode = parsedPayment.mode; // 'payment' 或 'subscription'
      this.subscriptionId = parsedPayment.subscriptionId;
      this.productDetails = parsedPayment.productDetails;

      console.log('Payment data prepared:', {
        mode: this.mode,
        amount: this.amount,
        currency: this.currency,
        hasOrderId: Boolean(this.orderId),
        hasTransactionId: Boolean(this.transactionId),
        hasSubscriptionId: Boolean(this.subscriptionId),
        hasProductDetails: Boolean(this.productDetails),
        hasCustomerInfo: Boolean(this.customerInfo),
      });
      
      // 檢查是否已經在處理付款流程，避免重複觸發
      if (this.isPaymentInProgress || this.paymentWindowOpened || this.paymentCompleted) {
        console.log('付款流程已在進行中，跳過重複初始化');
        return;
      }
      
      // 觸發實際的付款流程
      this.initializePayment();
    },
    
    /**
     * 初始化付款流程
     * 當收到付款資料後執行的邏輯
     */
    async initializePayment() {
      console.log('開始初始化付款流程...');
      
      // 防止重複執行
      if (this.isPaymentInProgress) {
        console.log('付款流程已在進行中，跳過重複初始化');
        return;
      }
      
      this.isPaymentInProgress = true; // 標記付款流程開始
      
      try {
        // 詳細檢查付款資料
        if (!this.paymentData) {
          throw new Error('無付款資料可處理');
        }
        
        console.log('付款資料檢查通過，開始呼叫 API...');
        
        // 呼叫後端 /charge API
        await this.callChargeApi();
        
      } catch (error) {
        console.error('付款初始化失敗:', error);
        this.isPaymentInProgress = false; // 付款失敗時重置狀態
        
        // 使用錯誤分析方法
        this.analyzeError(error, {
          step: 'initializePayment',
          hasPaymentData: !!this.paymentData,
          amount: this.amount,
          locationId: this.paymentData?.locationId || this.paymentData?.location_id
        });
      }
    },

    /**
     * 處理並分析錯誤類型
     * @param {Error} error - 錯誤物件
     * @param {object} context - 錯誤發生的上下文
     */
    analyzeError(error, context = {}) {
      console.error('錯誤分析:', error);
      const safeContext = { ...context };
      delete safeContext.paymentData;
      console.error('錯誤上下文:', safeContext);
      
      let userFriendlyMessage = error.message;
      let suggestions = [];
      let errorType = 'unknown';
      
      // 分析不同類型的錯誤
      if (error.message.includes('Cannot read properties')) {
        errorType = 'data_format_error';
        userFriendlyMessage = 'GHL 付款資料格式錯誤';
        suggestions.push('檢查 GHL 是否正確傳送付款資料');
        suggestions.push('確認 payment link 設定是否正確');
      } else if (error.message.includes('locationId')) {
        errorType = 'location_id_missing';
        userFriendlyMessage = '無法取得商店 ID (locationId)';
        suggestions.push('確認該 GHL 帳戶是否已正確設定');
        suggestions.push('檢查 payment link 是否包含正確的 locationId');
      } else if (error.message.includes('HTTP 404')) {
        errorType = 'api_not_found';
        userFriendlyMessage = 'API 端點不存在';
        suggestions.push('確認後端伺服器是否正在運行');
        suggestions.push('檢查 /charge API 路由是否正確設定');
      } else if (error.message.includes('HTTP 500')) {
        errorType = 'server_error';
        userFriendlyMessage = '伺服器內部錯誤';
        suggestions.push('檢查後端伺服器日誌');
        suggestions.push('確認資料庫連線是否正常');
        suggestions.push('檢查 NewebPay 設定是否正確');
      } else if (error.message.includes('Failed to fetch') || error.message.includes('網路連線')) {
        errorType = 'network_error';
        userFriendlyMessage = '無法連接到付款伺服器';
        suggestions.push('檢查網路連線');
        suggestions.push('確認伺服器是否正在運行');
        suggestions.push('檢查防火牆設定');
      }
      
      // 建立詳細的錯誤報告
      const errorReport = {
        timestamp: new Date().toISOString(),
        userMessage: userFriendlyMessage,
        originalError: error.message,
        suggestions: suggestions,
        context: safeContext,
        hasPaymentData: Boolean(this.paymentData),
        currentUrl: `${window.location.origin}${window.location.pathname}`,
        userAgent: navigator.userAgent
      };
      
      // 記錄錯誤分析結果
      clientLogger.error('payment_error_analyzed', {
        errorType,
        userMessage: userFriendlyMessage,
        originalError: error.message,
        suggestions,
        context: safeContext,
        hasPaymentData: !!this.paymentData,
      });
      
      // 顯示錯誤
      this.showError(userFriendlyMessage + (suggestions.length > 0 ? '\n\n建議檢查：\n• ' + suggestions.join('\n• ') : ''), errorReport);
    },

    /**
     * 呼叫後端 API 取得藍新金流參數
     */
    async callChargeApi() {
      if (this.mode === 'subscription') {
        await this.startCheckoutAgreement('subscription');
        return;
      }
      const apiEndpoint = '/charge';
      
      console.log(`正在呼叫後端 ${apiEndpoint} API...`);
      
      // 從 GHL 付款資料中提取 locationId
      const locationId = this.paymentData?.locationId || this.paymentData?.location_id;
      
      console.log('Payment context extracted:', {
        hasLocationId: Boolean(locationId),
        hasPaymentData: Boolean(this.paymentData),
      });
      
      if (!locationId) {
        throw new Error('無法取得 locationId，無法處理付款');
      }
      
      const validAmount = this.amount;
      
      // 最終驗證金額
      if (!validAmount || validAmount <= 0) {
        throw new Error('付款金額無效: ' + this.amount);
      }

      const requestBody = {
        locationId: locationId,
        amount: this.amount,
        itemDesc: `Order ${this.orderId || 'GHL_' + Date.now()}`,
        email: (this.paymentData && this.paymentData.contact && this.paymentData.contact.email) ||
               'customer@example.com',
        publishableKey: this.paymentData.publishableKey || 'default_publishable_key',
        ghlTransactionId: this.transactionId // 加入 GHL 提供的交易 ID
      };

      console.log(`Sending ${apiEndpoint} request:`, {
        mode: this.mode,
        amount: validAmount,
        fields: Object.keys(requestBody),
        hasEmail: Boolean(requestBody.email),
      });

      try {
        // 記錄 API 呼叫開始
        clientLogger.info('api_call_started', {
          endpoint: apiEndpoint,
          mode: this.mode,
          amount: validAmount,
          hasLocationId: !!locationId,
          hasSubscriptionId: !!this.subscriptionId,
        });
        
        // 檢查是否為相對路徑,如果是則使用完整 URL
        const baseUrl = window.location.origin;
        const apiUrl = apiEndpoint.startsWith('http') ? apiEndpoint : `${baseUrl}${apiEndpoint}`;
        
        console.log('API URL:', apiUrl);

        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        };

        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(requestBody)
        });

        console.log('Response status:', response.status);

        // 嘗試讀取回應內容
        let responseText;
        try {
          responseText = await response.text();
          console.log('Response received:', {
            status: response.status,
            responseLength: responseText.length,
            contentType: response.headers.get('content-type') || '',
          });
        } catch (readError) {
          console.error('無法讀取回應內容:', readError);
          
          clientLogger.error('api_call_failed', {
            endpoint: apiEndpoint,
            error: 'failed_to_read_response',
            message: readError.message,
          });
          
          throw new Error('伺服器回應無法讀取');
        }

        // 處理 HTTP 錯誤狀態
        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          
          // 嘗試解析錯誤訊息
          try {
            const errorData = JSON.parse(responseText);
            if (errorData.message) {
              errorMessage = errorData.message;
            } else if (errorData.error) {
              errorMessage = errorData.error;
            }
            
            // 加入更詳細的錯誤信息到 debug
            this.debugInfo = JSON.stringify({
              status: response.status,
              statusText: response.statusText,
              errorMessage: errorData.message || errorData.error || 'unknown',
              responseHeaders: Object.fromEntries(response.headers.entries())
            }, null, 2);
            
          } catch (parseError) {
            console.warn('無法解析錯誤回應為 JSON:', parseError);
            
            // 如果不是 JSON,檢查是否為 HTML 錯誤頁面
            if (responseText.includes('<html>') || responseText.includes('<!DOCTYPE')) {
              errorMessage = '伺服器返回 HTML 錯誤頁面,可能是路由問題或伺服器錯誤';
            } else {
              errorMessage = `${errorMessage}\n伺服器回應格式無法解析`;
            }
            
            this.debugInfo = JSON.stringify({
              status: response.status,
              statusText: response.statusText,
              responseLength: responseText.length,
              apiUrlOrigin: new URL(apiUrl).origin,
            }, null, 2);
          }
          
          clientLogger.error('api_call_failed', {
            endpoint: apiEndpoint,
            httpStatus: response.status,
            errorMessage,
            responseLength: responseText.length,
          });
          
          throw new Error(errorMessage);
        }

        // 嘗試解析成功回應
        let result;
        try {
          result = JSON.parse(responseText);
          console.log(`Backend ${apiEndpoint} response:`, {
            status: result.status,
            hasPayload: Boolean(result.payload),
            payloadLength: result.payload?.length || 0,
            hasMessage: Boolean(result.message || result.error),
          });
        } catch (parseError) {
          console.error('無法解析回應為 JSON:', parseError);
          
          clientLogger.error('api_call_failed', {
            endpoint: apiEndpoint,
            error: 'invalid_json_response',
            responseLength: responseText.length,
          });
          
          throw new Error('伺服器回應格式錯誤,無法解析 JSON');
        }

        if (result.status === 'success' && result.payload) {
          this.merchantOrderNo = result.merchantOrderNo || null;
          // 記錄 API 成功
          clientLogger.info('api_call_completed', {
            endpoint: apiEndpoint,
            hasPayload: !!result.payload,
            payloadLength: result.payload?.length || 0,
          });
          
          // 後端現在回傳完整的 HTML 表單,直接處理
          this.handlePaymentFormHTML(result.payload);
        } else {
          const errorMsg = result.message || result.error || '後端回傳未知錯誤';
          
          // 儲存詳細的錯誤信息
          this.debugInfo = JSON.stringify({
            resultStatus: result.status,
            hasPayload: Boolean(result.payload),
            responseLength: responseText.length,
            apiUrlOrigin: new URL(apiUrl).origin
          }, null, 2);
          
          clientLogger.error('api_call_failed', {
            endpoint: apiEndpoint,
            error: 'api_returned_error',
            errorMessage: errorMsg,
          });
          
          throw new Error(errorMsg);
        }
      } catch (error) {
        console.error(`${apiEndpoint} API 呼叫失敗:`, error);

        // 提供更詳細的錯誤信息
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          clientLogger.error('api_call_failed', {
            endpoint: apiEndpoint,
            error: 'fetch_type_error',
            message: error.message,
          });
          
          throw new Error('無法連接到伺服器,請檢查網路連線或伺服器是否正在運行');
        }
        
        // 如果是網路錯誤
        if (error.message.includes('Failed to fetch')) {
          clientLogger.error('api_call_failed', {
            endpoint: apiEndpoint,
            error: 'network_error',
            message: error.message,
          });
          
          throw new Error('網路連線失敗,請檢查:\n1. 伺服器是否正在運行\n2. API 端點是否正確\n3. 是否有 CORS 問題');
        }
        
        throw error;
      }
    },

    /**
     * 結帳訂閱：首次約定授權並建卡（ADR 0002）。金額與週期由後端依 payment_initiate_props 決定，
     * 這裡只轉送 GHL 原始欄位（不使用 parsePaymentInitiateProps 改寫過的 amount）。
     */
    async startCheckoutAgreement(kind) {
      const raw = this.paymentData || {};
      const locationId = raw.locationId || raw.location_id;
      const contactId = raw.contact && raw.contact.id;
      if (!locationId || !contactId) {
        throw new Error('缺少 locationId 或聯絡人資料，無法綁定信用卡');
      }

      const contextResponse = await axios.post('/api/payment-method/context', {
        locationId,
        contactId,
        publishableKey: raw.publishableKey,
        contactName: raw.contact.name,
      });
      const contextToken = contextResponse.data && contextResponse.data.contextToken;
      if (!contextToken) throw new Error('無法建立付款上下文');

      let response;
      try {
        response = await axios.post('/api/checkout/agreement/initialize', {
          kind,
          contextToken,
          paymentProps: {
            amount: raw.amount,
            currency: raw.currency,
            productDetails: raw.productDetails,
            subscriptionId: raw.subscriptionId,
            transactionId: raw.transactionId || raw.transaction_id,
            contact: { id: contactId },
            email: raw.contact.email,
          },
        });
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message;
        postToGhl(window.parent, buildErrorResponsePayload({ description: message, isSubscription: kind === 'subscription' }));
        throw new Error(message);
      }
      if (!response.data || !response.data.success || !response.data.formHtml) {
        throw new Error((response.data && response.data.message) || '產生授權表單失敗');
      }

      const firstPrice = raw.productDetails && raw.productDetails[0] && raw.productDetails[0].prices && raw.productDetails[0].prices[0];
      this.agreementIsTrial = Boolean(firstPrice && Number(firstPrice.trialPeriod) > 0);
      // 不使用一般付款的 /api/transaction/status 補查：訂閱必須等 attempt 完成
      this.merchantOrderNo = null;
      this.agreementAttempt = { contextToken, attemptId: response.data.attemptId };
      this.handlePaymentFormHTML(response.data.formHtml);
      this.startAgreementPolling();
    },

    startAgreementPolling() {
      if (this.agreementPoller) this.agreementPoller.stop();
      const { contextToken, attemptId } = this.agreementAttempt;
      const isSubscription = this.mode === 'subscription';

      this.agreementPoller = pollAgreementStatus({
        fetchStatus: async () => {
          const res = await axios.post('/api/checkout/agreement/status', { contextToken, attemptId });
          return res.data;
        },
        isWindowClosed: () => {
          if (!this.paymentWindowOpened || this.popupBlocked) return false;
          const paymentWindow = this.getPaymentWindowRef();
          return !paymentWindow || paymentWindow.closed;
        },
        onCompleted: (chargeId) => {
          this.agreementPoller = null;
          this.paymentCompleted = true;
          this.isPaymentInProgress = false;
          clientLogger.info('payment_success_received', { source: 'checkout_agreement', hasChargeId: Boolean(chargeId) });
          postToGhl(window.parent, buildSuccessResponsePayload({
            chargeId,
            subscriptionId: this.subscriptionId,
            isSubscription,
          }));
          this.forceClosePaymentWindow();
        },
        onFailed: (status) => {
          this.agreementPoller = null;
          const message = status === 'EXPIRED' ? '授權逾時，請重新結帳' : '信用卡授權失敗，請重新結帳';
          postToGhl(window.parent, buildErrorResponsePayload({ description: message, isSubscription }));
          this.forceClosePaymentWindow();
          this.showError(message, { attemptStatus: status });
        },
        onClosed: () => {
          this.agreementPoller = null;
          this.isPaymentInProgress = false;
          postToGhl(window.parent, buildCloseResponsePayload({ isSubscription }));
        },
        onTimeout: () => {
          this.agreementPoller = null;
          const message = '尚未收到授權結果，請稍後確認訂閱狀態或重新結帳';
          postToGhl(window.parent, buildErrorResponsePayload({ description: message, isSubscription }));
          this.forceClosePaymentWindow();
          this.showError(message, { attemptStatus: 'TIMEOUT' });
        },
      });
    },

    /**
     * 處理後端回傳的完整 HTML 表單
     * @param {string} paymentFormHTML - 完整的 HTML 表單字串
     */
    handlePaymentFormHTML(paymentFormHTML) {
      console.log('正在處理藍新金流 HTML 表單...');
      console.log('NewebPay form received:', {
        formLength: typeof paymentFormHTML === 'string' ? paymentFormHTML.length : 0,
      });

      // 儲存表單HTML以供重新開啟使用
      this.paymentFormHTML = paymentFormHTML;

      // 不自動開啟視窗，等待用戶手動點擊
      // 先嘗試開啟視窗，如果被阻擋則顯示手動開啟選項
      this.attemptToOpenWindow();
    },

    /**
     * 開啟付款視窗
     * @param {string} paymentFormHTML - 付款表單HTML（可選，使用儲存的HTML）
     */
    openPaymentWindow(paymentFormHTML = null) {
      const htmlToUse = paymentFormHTML || this.paymentFormHTML;
      if (!htmlToUse) {
        throw new Error('沒有可用的付款表單HTML');
      }

      // 檢查是否已有付款視窗開啟（防止重複開啟）
      const existingWindow = this.getPaymentWindowRef();
      if (existingWindow && !existingWindow.closed) {
        console.log('付款視窗已存在且未關閉，避免重複開啟');
        try {
          existingWindow.focus();
        } catch (e) {
          console.warn('無法將現有視窗帶到前景:', e);
        }
        return;
      }

      console.log('開啟新的付款視窗...');

      // 建立一個容器來放置 HTML 表單
      const container = document.createElement('div');
      container.innerHTML = htmlToUse;
      
      // 設定容器樣式，確保表單能正常顯示
      container.style.position = 'absolute';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.zIndex = '9999';

      // 將容器加入到 DOM
      document.body.appendChild(container);

      console.log('HTML 表單已插入到頁面，準備開啟新視窗');

      // 以「新視窗/分頁」開啟藍新頁面避免被父頁 iframe 限制（X-Frame-Options）
      const forms = container.querySelectorAll('form');
      if (!forms || forms.length === 0) {
        throw new Error('後端回傳 HTML 中找不到表單');
      }

      const form = forms[0];
      // 命名新視窗，便於 ReturnURL 使用 window.opener 回傳
      const name = `newebpay_popup_${Date.now()}`;
      this.paymentWindowName = name;

      // 嘗試預先開啟空白視窗
      try {
        // 根據螢幕大小調整視窗尺寸
        const screenWidth = window.screen.availWidth;
        const screenHeight = window.screen.availHeight;
        
        // 藍新付款頁需要約 520×760 才能完整顯示；小螢幕則盡量佔滿，避免開出難以察覺的小視窗
        const windowWidth = Math.max(360, Math.min(560, screenWidth - 40));
        const windowHeight = Math.max(560, Math.min(820, screenHeight - 60));
        
        // 計算視窗置中位置
        const left = Math.round((screenWidth - windowWidth) / 2);
        const top = Math.round((screenHeight - windowHeight) / 2);
        
        // 建立視窗參數，讓視窗在螢幕中央開啟
        const windowFeatures = [
          'noopener=no',
          'resizable=yes',
          'scrollbars=yes',
          'toolbar=no',
          'menubar=no',
          'location=no',
          'status=no',
          `width=${windowWidth}`,
          `height=${windowHeight}`,
          `left=${left}`,
          `top=${top}`
        ].join(',');
        
        console.log('開啟付款視窗參數:', windowFeatures);
        console.log('螢幕尺寸:', { screenWidth, screenHeight });
        console.log('視窗尺寸:', { windowWidth, windowHeight, left, top });
        
        const newWindow = window.open('about:blank', name, windowFeatures);
        this.setPaymentWindowRef(newWindow);
        try {
          if (newWindow) newWindow.focus();
        } catch (e) {
          console.warn('無法將付款視窗帶到前景:', e);
        }
        
        // 檢查是否成功開啟視窗
        const activeWindow = this.getPaymentWindowRef();
        if (!activeWindow || activeWindow.closed) {
          clientLogger.warn('payment_window_open_failed', {
            windowName: name,
            windowExists: !!activeWindow,
            windowClosed: activeWindow?.closed,
          });
          
          console.warn('視窗開啟失敗或被阻擋');
          this.popupBlocked = true;
          this.clearPaymentWindowRef();
          this.paymentWindowOpened = true; // 顯示指引界面
          
          // 清理DOM中的表單容器，但不提交表單
          setTimeout(() => {
            if (container.parentNode) {
              container.parentNode.removeChild(container);
            }
          }, 100);
          
          // 不繼續執行表單提交，等待用戶手動開啟
          return;
        } else {
          clientLogger.info('payment_window_opened_success', {
            windowName: name,
            windowWidth,
            windowHeight,
            screenWidth,
            screenHeight,
          });
          
          console.log('付款視窗已成功開啟');
          this.paymentWindowOpened = true;
          this.startWindowMonitoring();
        }
      } catch (e) {
        console.warn('開啟新視窗時發生錯誤：', e);
        this.popupBlocked = true;
        this.clearPaymentWindowRef();
        this.paymentWindowOpened = true; // 顯示指引界面
        
        // 清理DOM中的表單容器，但不提交表單
        setTimeout(() => {
          if (container.parentNode) {
            container.parentNode.removeChild(container);
          }
        }, 100);
        
        // 不繼續執行表單提交，等待用戶手動開啟
        return;
      }

      // 設定提交目標：若能命名視窗則使用名稱；否則至少使用 _blank 開新分頁
      const submissionWindow = this.getPaymentWindowRef();
      form.setAttribute('target', submissionWindow ? name : '_blank');

      // 提交表單
      this.$nextTick(() => {
        console.log('提交藍新表單，target=', form.getAttribute('target'));
        try {
          form.submit();
          console.log('表單已提交');
          
          // 如果沒有成功開啟命名視窗，標記為可能被阻擋
          if (!this.getPaymentWindowRef()) {
            this.popupBlocked = true;
            this.paymentWindowOpened = true; // 仍然嘗試顯示指引界面
          }
          
          // 清理DOM中的表單容器
          setTimeout(() => {
            if (container.parentNode) {
              container.parentNode.removeChild(container);
            }
          }, 1000);
          
        } catch (submitErr) {
          console.error('表單提交失敗：', submitErr);
          this.isPaymentInProgress = false;
          throw submitErr;
        }
      });

      console.log('付款流程已在新視窗進行，等待 ReturnURL 回傳結果');
    },

    /**
     * 嘗試開啟視窗（僅測試，不提交表單）
     */
    attemptToOpenWindow() {
      clientLogger.info('attempt_open_payment_window', {
        hasFormHTML: !!this.paymentFormHTML,
      });

      // 直接開正式尺寸的付款視窗；被擋時 openPaymentWindow 會改顯示手動開啟按鈕。
      // 不先開 1×1 測試視窗：部分瀏覽器會把後續付款視窗沿用成極小尺寸
      try {
        this.openPaymentWindow();
      } catch (error) {
        clientLogger.error('popup_test_exception', {
          error: error.message,
          errorType: error.name,
        });
        console.warn('開啟付款視窗時發生錯誤:', error);
        this.popupBlocked = true;
        this.paymentWindowOpened = true;
      }
    },

    /**
     * 手動開啟付款視窗（用於彈出視窗被阻擋時）
     */
    openPaymentWindowManually() {
      if (!this.paymentFormHTML) {
        clientLogger.error('manual_open_no_form_html', {
          paymentData: !!this.paymentData,
          mode: this.mode,
        });
        
        this.showError('無法重新開啟付款視窗：沒有可用的付款表單資料');
        return;
      }

      clientLogger.info('manual_open_clicked', {
        hasFormHTML: !!this.paymentFormHTML,
        currentState: {
          isPaymentInProgress: this.isPaymentInProgress,
          paymentWindowOpened: this.paymentWindowOpened,
          popupBlocked: this.popupBlocked,
        },
      });

      // 檢查是否已有付款視窗開啟
      const existingWindow = this.getPaymentWindowRef();
      if (existingWindow && !existingWindow.closed) {
        console.log('付款視窗已開啟，將其帶到前景');
        try {
          existingWindow.focus();
        } catch (e) {
          console.warn('無法將現有視窗帶到前景:', e);
        }
        return;
      }

      // 防止重複操作
      if (this.isPaymentInProgress && this.paymentWindowOpened && !this.popupBlocked) {
        console.log('付款流程已在進行中，避免重複開啟');
        return;
      }

      console.log('手動開啟付款視窗...');

      // 重置狀態
      this.popupBlocked = false;
      this.paymentWindowOpened = false;
      
      try {
        this.openPaymentWindow();
      } catch (error) {
        clientLogger.error('manual_open_window_failed', {
          error: error.message,
          errorType: error.name,
        });
        
        console.error('手動開啟付款視窗失敗:', error);
        this.showError('無法開啟付款視窗，請檢查瀏覽器設定或嘗試重新整理頁面');
      }
    },

    /**
     * 開始監控付款視窗狀態
     */
    startWindowMonitoring() {
      if (this.windowCheckInterval) {
        clearInterval(this.windowCheckInterval);
      }

      clientLogger.info('start_window_monitoring');

      this.windowCheckInterval = setInterval(() => {
        const paymentWindow = this.getPaymentWindowRef();
        if (paymentWindow && paymentWindow.closed) {
          clientLogger.warn('payment_window_closed', {
            paymentCompleted: this.paymentCompleted,
            timestamp: new Date().toISOString(),
          });
          
          console.log('付款視窗已關閉');
          clearInterval(this.windowCheckInterval);
          this.windowCheckInterval = null;
          this.clearPaymentWindowRef();
          
          // 視窗關閉但沒有收到完成訊息，可能是用戶手動關閉
          if (!this.paymentCompleted) {
            void this.confirmClosedPayment();
          }
        }
      }, 1000);

      // 設定超時檢查（15分鐘）
      setTimeout(() => {
        if (this.windowCheckInterval) {
          clearInterval(this.windowCheckInterval);
          this.windowCheckInterval = null;
          console.warn('付款視窗監控超時');
        }
      }, 15 * 60 * 1000);
    },

    /**
     * ReturnURL 的 postMessage 遺失時，以已建立的 MerchantOrderNo 補查本地交易狀態。
     */
    async confirmClosedPayment(attempt = 0) {
      this.paymentStatusPollTimer = null;
      if (this.paymentCompleted || !this.merchantOrderNo) {
        this.isPaymentInProgress = false;
        this.paymentWindowOpened = true;
        this.popupBlocked = false;
        return;
      }

      try {
        const response = await fetch(
          `/api/transaction/status?merchantOrderNo=${encodeURIComponent(this.merchantOrderNo)}`,
          { headers: { Accept: 'application/json' } },
        );
        const result = await response.json();
        const status = result?.data?.status;
        if (response.ok && result?.status === 'success' && status === 'success') {
          this.paymentCompleted = true;
          this.isPaymentInProgress = false;
          clientLogger.info('payment_success_received', {
            source: 'transaction_status_fallback',
            hasTradeNo: Boolean(result.data.newebpayTradeNo),
          });
          postToGhl(window.parent, buildSuccessResponsePayload({
            chargeId: result.data.newebpayTradeNo || undefined,
          }));
          return;
        }
      } catch (error) {
        clientLogger.warn('payment_status_fallback_failed', {
          attempt: attempt + 1,
          error: error?.message || 'request_failed',
        });
      }

      if (attempt < 9 && !this.paymentCompleted) {
        this.paymentStatusPollTimer = setTimeout(() => {
          void this.confirmClosedPayment(attempt + 1);
        }, 1000);
        return;
      }

      console.log('付款視窗已關閉，但尚未確認交易結果');
      this.isPaymentInProgress = false;
      this.paymentWindowOpened = true;
      this.popupBlocked = false;
    },

    /**
     * 強制關閉付款視窗（多次嘗試）
     */
    forceClosePaymentWindow() {
      const paymentWindow = this.getPaymentWindowRef();
      if (!paymentWindow) {
        console.log('No payment window reference to close');
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;
      
      const attemptClose = () => {
        attempts++;
        try {
          if (paymentWindow.closed) {
            console.log(`Payment window already closed after ${attempts} attempts`);
            this.clearPaymentWindowRef();
            return;
          }
          
          console.log(`Attempting to close payment window (attempt ${attempts}/${maxAttempts})`);
          paymentWindow.close();
          
          // 檢查是否成功關閉
          setTimeout(() => {
            if (!paymentWindow.closed && attempts < maxAttempts) {
              attemptClose();
            } else if (paymentWindow.closed) {
              console.log(`Payment window successfully closed after ${attempts} attempts`);
              this.clearPaymentWindowRef();
            } else {
              console.warn(`Failed to close payment window after ${maxAttempts} attempts`);
            }
          }, 200);
          
        } catch (e) {
          console.warn(`Failed to close payment window (attempt ${attempts}):`, e);
          if (attempts < maxAttempts) {
            setTimeout(attemptClose, 500);
          }
        }
      };

      // 立即嘗試第一次
      attemptClose();
    },

    /**
     * 顯示錯誤訊息
     * @param {string} message - 錯誤訊息
     * @param {object} debug - 調試信息
     */
    showError(message, debug = null) {
      this.hasError = true;
      this.errorMessage = message;
      this.debugInfo = debug ? JSON.stringify(debug, null, 2) : null;
      
      console.error('付款錯誤:', message);
      if (debug) {
        console.error('調試信息:', debug);
      }
    },

    /**
     * 複製當前網址到剪貼簿
     */
    /**
     * 重試付款流程
     */
    async retryPayment() {
      this.isRetrying = true;
      this.hasError = false;
      this.errorMessage = '';
      this.debugInfo = null;
      this.isPaymentInProgress = false; // 重置付款狀態

      try {
        await this.initializePayment();
      } catch (error) {
        console.error('重試失敗:', error);
        this.analyzeError(error, {
          step: 'retryPayment',
          retryAttempt: true,
          hasPaymentData: Boolean(this.paymentData)
        });
      } finally {
        this.isRetrying = false;
      }
    },

    /**
     * 返回上一頁
     */
    goBack() {
      console.log('取消付款按鈕被點擊');
      
      // 先清理付款狀態
      this.isPaymentInProgress = false;
      this.paymentWindowOpened = false;
      this.paymentCompleted = false;
      this.popupBlocked = false;
      
      // 關閉付款視窗（如果存在）
      const paymentWindow = this.getPaymentWindowRef();
      if (paymentWindow && !paymentWindow.closed) {
        try {
          paymentWindow.close();
          console.log('已關閉付款視窗');
        } catch (e) {
          console.warn('無法關閉付款視窗:', e);
        }
      }
      this.clearPaymentWindowRef();
      
      // 清理計時器
      if (this.windowCheckInterval) {
        clearInterval(this.windowCheckInterval);
        this.windowCheckInterval = null;
      }
      if (this.paymentStatusPollTimer) {
        clearTimeout(this.paymentStatusPollTimer);
        this.paymentStatusPollTimer = null;
      }

      // 嘗試通知父視窗關閉或返回
      try {
        if (window.parent && window.parent !== window) {
          console.log('發送取消訊息給父視窗');
          // 發送取消訊息
          postToGhl(window.parent, buildCloseResponsePayload());
          
        } else {
          console.log('不在 iframe 中，嘗試關閉視窗或返回');
          // 如果不在 iframe 中，嘗試使用 history API
          if (window.history.length > 1) {
            window.history.back();
          } else {
            // 嘗試關閉視窗
            const closed = window.close();
            if (!closed) {
              // 如果無法關閉，顯示提示
              alert('請手動關閉此頁面');
            }
          }
        }
      } catch (error) {
        console.error('取消付款時發生錯誤:', error);
        // 備用方案：重新載入頁面到一個安全的狀態
        if (confirm('無法正常返回，是否重新載入頁面？')) {
          window.location.reload();
        }
      }
    }
  }
};
</script>

<style scoped>
.checkout-container {
  min-height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
}

.checkout-content {
  background: white;
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  text-align: center;
  max-width: 500px;
  width: 95%;
  border: 1px solid rgba(0, 0, 0, 0.05);
  min-height: auto;
}

h1 {
  color: #1a202c;
  margin: 0 0 1.5rem 0;
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.spinner {
  border: 4px solid #e3f2fd;
  border-top: 4px solid #2196f3;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.loading h1, .error-container h1 {
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

.loading p, .error-message {
  animation: fadeInUp 0.8s ease-out 0.4s both;
}

.error-actions {
  animation: fadeInUp 0.8s ease-out 0.6s both;
}

.loading p {
  color: #718096;
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .checkout-content {
    padding: 2rem;
    margin: 1rem;
  }
  
  h1 {
    font-size: 1.4rem;
  }
}

/* 錯誤顯示樣式 */
.error-container {
  text-align: center;
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.error-message {
  background: linear-gradient(145deg, #fef2f2, #fecaca);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1rem 0;
  color: #dc2626;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02);
}

.error-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin: 2rem 0;
}

.retry-btn, .back-btn {
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-btn {
  background: linear-gradient(135deg, #2196f3, #1976d2);
  color: white;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

.retry-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(33, 150, 243, 0.4);
}

.retry-btn:disabled {
  background: #9e9e9e;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.back-btn {
  background: linear-gradient(135deg, #6c757d, #495057);
  color: white;
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
}

.back-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(108, 117, 125, 0.4);
}

.debug-info {
  text-align: left;
  margin-top: 2rem;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 1rem;
}

.debug-info summary {
  cursor: pointer;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.debug-info pre {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 0.75rem;
  font-size: 0.875rem;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* 付款視窗指引樣式 */
.payment-window-guidance {
  text-align: center;
  animation: fadeInUp 0.6s ease-out;
}

.guidance-content {
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 2rem;
  margin: 1.5rem 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.guidance-content h3 {
  color: #111827;
  font-size: 1.1rem;
  margin: 0.5rem 0 1rem 0;
  font-weight: 600;
}

.main-instruction {
  font-size: 1.1rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1.5rem;
}

.guidance-steps {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1.5rem 0;
}

.step {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  text-align: left;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  background: #3b82f6;
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.step-text {
  color: #374151;
  font-weight: 400;
  font-size: 0.95rem;
}

.open-payment-step p {
  color: #4b5563;
  margin: 0 0 1.25rem 0;
  font-size: 0.95rem;
  line-height: 1.5;
}

.primary-open-btn {
  width: 100%;
  background: #2563eb;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.05rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.primary-open-btn:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.primary-open-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.warning-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

.agreement-consent {
  max-width: 360px;
  margin: 0 auto 0.75rem;
  font-size: 0.85rem;
  line-height: 1.5;
  color: #4b5563;
}

.manual-open-btn {
  background: #f59e0b;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.manual-open-btn:hover {
  background: #d97706;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.manual-open-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.backup-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
}

.backup-btn {
  background: #6b7280;
  color: white;
  border: none;
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.backup-btn:hover {
  background: #4b5563;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.backup-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.cancel-btn {
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.cancel-btn:hover {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.cancel-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.status-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 2rem;
  padding: 1rem;
  background: #ecfdf5;
  border: 1px solid #10b981;
  border-radius: 6px;
  color: #065f46;
  font-weight: 500;
}

.pulse-dot {
  width: 12px;
  height: 12px;
  background: #10b981;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.payment-success {
  text-align: center;
  animation: fadeInUp 0.6s ease-out;
}

.success-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  animation: bounce 1s ease-in-out;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.1);
  }
}

@keyframes bounce {
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0, 0, 0);
  }
  40%, 43% {
    transform: translate3d(0, -15px, 0);
  }
  70% {
    transform: translate3d(0, -7px, 0);
  }
  90% {
    transform: translate3d(0, -2px, 0);
  }
}

@media (max-width: 768px) {
  .guidance-content {
    padding: 1.5rem;
  }
  
  .step {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
  
  .backup-actions {
    gap: 0.75rem;
  }
  
  .backup-btn, .cancel-btn, .manual-open-btn {
    padding: 0.875rem 1.25rem;
    font-size: 0.9rem;
  }
}

/* In-App Browser 專用引導樣式 */
.in-app-browser-notice {
  background: linear-gradient(145deg, #dbeafe, #bfdbfe);
  border: 2px solid rgba(59, 130, 246, 0.4);
  border-radius: 16px;
  padding: 2rem;
  margin: 1.5rem 0;
  text-align: center;
  animation: slideInUp 0.5s ease-out;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.15);
}

.in-app-browser-notice h3 {
  color: #1e40af;
  font-size: 1.1rem;
  margin: 0.5rem 0 1rem 0;
  font-weight: 700;
}

.notice-description {
  color: #374151;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-size: 1rem;
}

/* App 專用引導步驟樣式 */
.app-specific-guide {
  margin: 1.5rem 0;
  text-align: left;
}

.guide-step {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  margin-bottom: 0.5rem;
  background: transparent;
  border: none;
  border-left: 3px solid #3b82f6;
  transition: all 0.2s ease;
}

.guide-step:hover {
  background: #f8fafc;
  border-left-color: #2563eb;
}

.step-icon {
  font-size: 1.4rem;
  flex-shrink: 0;
  width: 1.8rem;
  height: 1.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-content {
  flex: 1;
  color: #1e293b;
  line-height: 1.5;
  font-size: 0.95rem;
}

.step-content strong {
  color: #0f172a;
  font-weight: 600;
  margin-right: 0.25rem;
}

/* 選單圖示樣式 */
.menu-icon {
  display: inline-block;
  font-weight: bold;
  font-size: 1.2rem;
  color: #2563eb;
  background: #dbeafe;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  margin: 0 0.15rem;
  vertical-align: middle;
  white-space: nowrap;
}

.menu-icon.vertical-dots {
  font-size: 1.4rem;
  line-height: 1;
  padding: 0.15rem 0.35rem;
}

.browser-suggestions {
  margin-top: 1rem;
  padding: 1rem;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
}

.browser-suggestions p {
  color: #4b5563;
  font-size: 0.9rem;
  margin: 0;
  font-weight: 400;
}

/* 移動端優化 */
@media (max-width: 768px) {
  .checkout-content {
    max-width: 100%;
    width: 95%;
    padding: 1.5rem 1rem;
  }
  
  .app-specific-guide {
    margin: 1rem 0;
  }
  
  .guide-step {
    padding: 0.75rem 0.875rem;
    gap: 0.6rem;
  }
  
  .step-icon {
    font-size: 1.25rem;
    width: 1.6rem;
    height: 1.6rem;
  }
  
  .step-content {
    font-size: 0.9rem;
    line-height: 1.4;
  }
  
  .menu-icon {
    font-size: 1.1rem;
    padding: 0.1rem 0.35rem;
  }
  
  .menu-icon.vertical-dots {
    font-size: 1.3rem;
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
